import
  React, {
  useEffect,
  useRef,
  useState
} from 'react';

import
  styled
from 'styled-components';

import {
  withCommon
} from 'common/hocs';

import {
  DataStore,
  FontFamilies,
  Logger,
  Theme
} from 'common';

import PaystackPop from '@paystack/inline-js';

import {
  OrderProvider
} from 'providers';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background-color: ${Theme.Content.Background};
`;

const TimerDisplay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  z-index: 1001;
  background-color: ${Theme.Content.Background};
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Regular};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimerText = styled.span`
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Bold};
  font-size: 14px;
  width: 40px;
  margin-left: 5px;
`;

const EventPaymentFrameUI = props => {
  const [expiryTime, setExpiryTime] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const checkIntervalRef = useRef(null);
  const popupRef = useRef(null);
  const transactionIdRef = useRef(null);
  const paymentSuccessRef = useRef(false);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(props.common.location.search);
    const accessCode = searchParams.get('access_code');
    const expiryTimeParam = searchParams.get('expiry');
    const orderIdParam = searchParams.get('orderId');
    const cancelOnBackParam = (searchParams.get('cancelOnBack') ?? 'true') === 'true';
    
    if (!accessCode) {
      Logger.error('EventPaymentFrame', 'init', 'No access code provided');
      props.common.showToast('Payment session could not be started');
      props.common.navigate(-1);
      return;
    }

    if (!expiryTimeParam) {
      Logger.error('EventPaymentFrame', 'init', 'No payment expiry time provided');
      props.common.navigate(-1);
      return;
    }

    if (orderIdParam) {
      setOrderId(orderIdParam);
    }

    setExpiryTime(parseInt(expiryTimeParam));

    const now = Date.now();
    const secondsRemaining = Math.max(0, Math.floor((parseInt(expiryTimeParam) - now) / 1000));
    setRemainingTime(formatTime(secondsRemaining));

    // if we land on this page with less than 40 seconds remaining, we need to navigate to the expired page
    if (secondsRemaining <= 40) {
      Logger.error('EventPaymentFrame', 'init', 'Payment session expired');
      props.common.navigate(`/payment-result/expired?order_id=${orderIdParam}`);
      return;
    }

    const popup = new PaystackPop();

    popupRef.current = popup;

    if (popup.backgroundIframe?.style) {
      popup.backgroundIframe.style.backgroundColor = 'transparent';
    }
    if (popup.checkoutIframe?.style) {
      popup.checkoutIframe.style.opacity = '0';
      popup.checkoutIframe.style.top = '40px';
      popup.checkoutIframe.style.height = 'calc(100vh - 40px)';
    }

    popup.resumeTransaction(accessCode, {
      onLoad: (transaction) => {
        transactionIdRef.current = transaction.id;

        if (popupRef.current.checkoutIframe?.style) {
          popupRef.current.checkoutIframe.style.opacity = '1';
        }

        props.common.hideLoading();
      },
      onSuccess: (transaction) => {
        paymentSuccessRef.current = true;
        const url = new URL(transaction.redirecturl);
        const queryParams = url.search;
        props.common.navigate(`/payment-result/success${queryParams}&contact=${encodeURIComponent(DataStore.get('LOGGED_IN_EMAIL') || '')}`, { replace: true });
      },
      onError: (error) => {
        Logger.error('EventPaymentFrame', 'paymentError', error);
        props.common.showToast('Oops! Something didn\'t go as planned. Please try again.');
        props.common.navigate(-1);
      },
      onCancel: () => {
        if (cancelOnBackParam && !isNaN(orderIdParam)) {
          OrderProvider.cancelOrder(parseInt(orderIdParam))
            .catch(e => Logger.error('EventPaymentFrame', 'cancelOrder', e));
        }
        
        if (transactionIdRef.current) {
          transactionIdRef.current = null;
          props.common.navigate(-1);
        }
      }
    });

    return () => {
      // Clean up interval on unmount
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }

      if (popupRef.current && transactionIdRef.current && !paymentSuccessRef.current) {
        const tr = transactionIdRef.current;
        transactionIdRef.current = null;
        popupRef.current.cancelTransaction(tr);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!expiryTime) return;

    const setupExpiryCheck = () => {
      checkIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const secondsRemaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
        
        // Update the remaining time display
        setRemainingTime(formatTime(secondsRemaining));
        
        if (secondsRemaining <= 0) {
          // Session expired
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
          }
          Logger.error('EventPaymentFrame', 'checkExpiry', 'Payment session expired');
          props.common.navigate(`/payment-result/expired?order_id=${orderId}`);
        }
      }, 1000); // Update every second
    };
    
    setupExpiryCheck();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [expiryTime, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
      {remainingTime && (
        <TimerDisplay>
          Payment expires in: <TimerText>{remainingTime}</TimerText>
        </TimerDisplay>
      )}
    </Container>
  );
};

export const EventPaymentFrame = withCommon(EventPaymentFrameUI, {
  showHeader: false,
  showFooter: false,
  showLoadingOnLoad: true
});
